/**
 * useUserTimezone — resolves the dispatcher's profile timezone from the API.
 *
 * Phase 9 / Plan 09-05 — DEC-004 timezone source (WARNING 3 closed end-to-end).
 * Calls GET /v1/users/me/profile and extracts `profile.timezone`.
 *
 * DEC-004 (NON-NEGOTIABLE) — resolves the dispatcher's real profile timezone so
 * "as of HH:MM" / Created / staleness labels are correct for non-Central
 * dispatchers. Falls back to America/Chicago (DEFAULT_TZ) only until the query
 * resolves.
 *
 * Spec: §44 (local-by-context timestamps); DEC-004; 09-05-PLAN.md Task 1.
 */

import { useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type { UserProfile } from '@lodex-pro/shared';
import { DEFAULT_TZ } from './timestamp.js';

/** TanStack Query cache key for the user profile endpoint. */
export const USER_PROFILE_QUERY_KEY = ['users', 'me', 'profile'] as const;

/** Response envelope from GET /v1/users/me/profile. */
interface UserProfileResponse {
  readonly profile: UserProfile;
}

/**
 * Returns the full user profile query result. Useful when the consumer also
 * needs the `userId` in addition to `timezone`.
 *
 * §58 PR 3: refreshed every 5 minutes and on window focus, because the profile also carries
 * the hauler's FEATURES, which a Paelode admin can change at any time (the timezone rarely
 * changes, but refreshing it costs nothing).
 */
export function useUserProfile(): ReturnType<typeof useApiQuery<UserProfileResponse>> {
  const opts = useApiOpts();
  return useApiQuery<UserProfileResponse>(opts, '/v1/users/me/profile', {
    queryKey: USER_PROFILE_QUERY_KEY,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Returns the dispatcher's profile timezone string (IANA format, e.g. "America/New_York").
 *
 * Falls back to DEFAULT_TZ ('America/Chicago') until the profile query resolves.
 * This is the PRIMARY surface for DEC-004 compliance — pass the returned value as
 * the `timeZone` param to all staleness / timestamp formatters.
 */
export function useUserTimezone(): string {
  const { data } = useUserProfile();
  return data?.profile.timezone ?? DEFAULT_TZ;
}
